import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Link, Outlet } from "react-router-dom";
import Sidebar from "./components/Sidebar.jsx";
import Header from "./components/Header.jsx";
import { sidebarWidths } from "../theme/index.js";
import { useSnackbar } from "notistack";
import { useSetRecoilState } from "recoil";
import { notificationAtom } from "../recoil/atoms/notificationAtom.js";
import { backendGet } from "../services/dataService.js";
import VersionText from "../theme/components/VersionText.jsx";
import { colorsFoundation } from "../theme/tokens/colors-foundation.js";

import { typographyTokens } from "../theme/tokens/typography.js";
import { dimensions } from "../theme/index.js";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { useBreadcrumb } from "../contexts/BreadcrumbContext.jsx";
import EnvironmentBanner from "../components/EnvironmentBanner.jsx";
import { activeBrand } from "../theme/brands/index.js";

const logo_src = activeBrand.assets.logo;

const LayoutGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sidebarWidth",
})(({ theme, sidebarWidth }) => ({
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gridTemplateRows: "auto 1fr",
  gridTemplateAreas: '"header" "content"',
  backgroundColor: theme.palette.background.page,
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: `${sidebarWidth} minmax(0, 1fr)`,
    gridTemplateRows: "1fr",
    gridTemplateAreas: '"sidebar main"',
    transition: "grid-template-columns 0.3s ease",
  },
}));

const MainColumn = styled(Box)(({ theme }) => ({
  gridArea: "main",
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    gridArea: "header / header / content / content",
  },
}));

// The environment banner floats OVER the very top of the page (fixed overlay)
// instead of occupying a layout row, so it does not push anything down — it
// simply overlaps the top strip of the sidebar, header and content.
const BannerOverlay = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  width: "100%",
  zIndex: theme.zIndex.appBar + 2,
}));

const HeaderWrapper = styled(Box)(({ theme }) => ({
  gridArea: "header",
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar,
  backgroundColor: theme.palette.background.paper,
  // Grey divider line between header and page content (matches the dummy).
  borderBottom: `1px solid ${colorsFoundation.gray[200]}`,
}));

const SidebarWrapper = styled(Box)(({ theme }) => ({
  gridArea: "sidebar",
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    height: "100vh",
    padding: 0,
    backgroundColor: theme.palette.background.sidebar,
    minWidth: sidebarWidths.minimized,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  gridArea: "content",
  flex: 1,
  padding: theme.spacing(2),
  // Consistent top breathing room for every page (previously only the Schäden
  // page added this locally, so other pages hugged the header).
  paddingTop: theme.spacing(2.5),
  width: "100%",
  backgroundColor: theme.palette.background.page,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(2.5),
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(5),
    paddingTop: theme.spacing(2.5),
  },
}));

const ContentInner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "maxContentWidth",
})(({ theme, maxContentWidth }) => ({
  width: "100%",
  margin: "0 auto",
  // Full-bleed by default so pages use the whole content area like the click
  // dummy. A page that needs a narrower column passes an explicit maxContentWidth.
  maxWidth: maxContentWidth || "100%",
  overflowX: "visible",
}));

const FooterWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: `${dimensions.md} 0`,
  borderTop: `1px solid ${colorsFoundation.neutral[200]}`,
  backgroundColor: theme.palette.background.paper,
  marginTop: "auto",
}));

const BreadcrumbArea = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  marginBottom: 20,
  flexWrap: "wrap",
});

export default function BackendLayout({
  headerSlot,
  sidebarSlot,
  children,
  maxContentWidth,
}) {
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const setNotifications = useSetRecoilState(notificationAtom);
  const { breadcrumbs } = useBreadcrumb();

  const handleSidebarToggle = (minimized) => {
    setIsSidebarMinimized(minimized);
  };

  const sidebarWidth = isSidebarMinimized
    ? sidebarWidths.minimized
    : sidebarWidths.expanded;

  const app_env = import.meta.env.VITE_APP_ENV || "production";
  const showEnvBanner = app_env !== "production";

  useEffect(() => {
    let isActive = true;

    backendGet("data/notifications", {
      label: "Notification",
      options: {
        params: {
          sort: "-created_at",
          filter: { seen: false },
        },
      },
      setResult: (response) => {
        if (isActive) {
          setNotifications(response?.data || []);
        }
      },
      enqueueSnackbar: enqueueSnackbar,
    });

    return () => {
      isActive = false;
    };
  }, [enqueueSnackbar, setNotifications]);

  const headerContent = headerSlot ?? <Header />;
  const sidebarContent = sidebarSlot ?? (
    <Sidebar logoSrc={logo_src} onToggle={handleSidebarToggle} />
  );
  const content = children ?? <Outlet />;

  return (
    <>
      <LayoutGrid sidebarWidth={sidebarWidth}>
        {showEnvBanner && (
          <BannerOverlay>
            <EnvironmentBanner />
          </BannerOverlay>
        )}
        <SidebarWrapper>{sidebarContent}</SidebarWrapper>
        <MainColumn>
          <HeaderWrapper>{headerContent}</HeaderWrapper>
          <ContentWrapper>
            <ContentInner maxContentWidth={maxContentWidth}>
              <BreadcrumbArea sx={{ display: { md: "none" } }}>
                {breadcrumbs.map((crumb, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    {index > 0 && (
                      <ChevronRightIcon
                        sx={{
                          fontSize: "24px",
                          color: "text.primary",
                        }}
                      />
                    )}
                    <Typography
                      component={Link}
                      to={crumb.path}
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        color: "text.primary",
                        textDecoration: "none",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      {crumb.name}
                    </Typography>
                  </Box>
                ))}
              </BreadcrumbArea>

              {content}
            </ContentInner>
          </ContentWrapper>
          <FooterWrapper>
            <Typography
              variant="caption"
              sx={{
                color: colorsFoundation.neutral[500],
                fontFamily: typographyTokens.family.body,
                textAlign: "center",
                display: "block",
              }}
            >
              <VersionText />
            </Typography>
          </FooterWrapper>
        </MainColumn>
      </LayoutGrid>
    </>
  );
}

BackendLayout.propTypes = {
  headerSlot: PropTypes.node,
  sidebarSlot: PropTypes.node,
  children: PropTypes.node,
  maxContentWidth: PropTypes.string,
};
